import { JSX, useMemo } from "react"

import { InventoryBadges, InventoryBadgesProps } from "./InventoryBadges"
import { Image } from "~/src/components/Image"
import { StockIndicator } from "~/src/components/StockIndicator"
import { Format } from "~/src/lib/format"

export type InventoryItemDetailsProps = {
  /**
   * Product name/title
   */
  name: string
  /**
   * Cost per unit in dollars
   */
  cost: number
  /**
   * Total units sold
   */
  unitsSold: number
  /**
   * Date when inventory was last ordered
   */
  lastOrderedDate: string
  /**
   * Whether stock is required or limitless
   */
  inventoryRequired: boolean
  /**
   * Current stock quantity. Null if inventory is not required
   */
  quantity?: number
  /**
   * Product image source URL
   */
  imageSrc?: string
  /**
   * Link to order history
   */
  historyHref?: string
  /**
   * Value of in stock inventory
   */
  inStockValue?: number
} & Omit<InventoryBadgesProps, "className">

/**
 * A responsive product details card component that displays product information
 * in a clean, structured layout. Adapts between mobile and desktop views using
 * container queries.
 *
 * @example
 * ```tsx
 * <InventoryItemDetails
 *   name="Glass Mug"
 *   id="12345"
 *   costPerUnit={2.25}
 *   totalUnitsSold={482}
 *   lastOrderedDate="4/22/25"
 *   stockQuantity={14}
 *   inventoryRequired={true}
 *   imageSrc="/path/to/image.jpg"
 *   sellerRank={1}
 *   orderHistoryHref="/order-history/12345"
 * />
 * ```
 */

export function InventoryItemDetails(props: InventoryItemDetailsProps): JSX.Element {
  const {
    cost,
    eGift,
    hiddenFromStore,
    imageSrc,
    lastOrderedDate,
    name,
    onDemand,
    historyHref,
    sellerRank,
    inventoryRequired,
    unitsSold,
    inStockValue,
  } = props
  const quantity = inventoryRequired ? props.quantity : Infinity
  const hasInStockValue = inStockValue != null && inStockValue > 0

  const productDetails: { label: string; value: string | number }[] = useMemo(
    () =>
      [
        { label: "Product name:", value: name },
        unitsSold != null && { label: "Total units sold:", value: unitsSold },
        hasInStockValue && {
          label: "Value of in stock inventory:",
          value: Format.currency(inStockValue),
        },
        lastOrderedDate != null && { label: "Inventory last ordered on:", value: lastOrderedDate },
      ].filter((x) => x != null && typeof x !== "boolean"),
    [name, cost, unitsSold, lastOrderedDate]
  )

  return (
    <div className="h-full w-full @container">
      <div className="flex h-full w-full flex-col gap-6 @lg:flex-row">
        {/* Product Image */}
        <section className="w-full shrink-0 @lg:w-fit">
          <Image className="aspect-square h-auto w-full rounded-lg @lg:h-72 @lg:w-72" src={imageSrc} alt={name} />
        </section>

        {/* Product Details */}
        <section className="flex grow flex-col gap-3">
          <hgroup className="flex flex-col gap-3">
            <h1 className="text-lg font-medium text-gray-900">Details</h1>

            <InventoryBadges
              sellerRank={sellerRank}
              eGift={eGift}
              onDemand={onDemand}
              hiddenFromStore={hiddenFromStore}
            />
          </hgroup>

          {/* Product details */}
          <ul className="flex flex-col gap-1">
            {productDetails.map(({ label, value }) => (
              <li key={label}>
                <span className="text-gray-500">{label}</span>&nbsp;
                <span className="text-gray-500">{value}</span>
              </li>
            ))}

            {/* Order history link */}
            {historyHref && (
              <li>
                <a href={historyHref} className="text-blue-600 hover:text-blue-800">
                  See order history
                </a>
              </li>
            )}
          </ul>

          {/* Stock indicator */}
          <StockIndicator quantity={quantity} />
        </section>
      </div>
    </div>
  )
}
